import { Alert, Box, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import persistanceStore from "stores/persistanceStore";
import useDebounce from "hooks/useDebounce";
import { useQuery } from "react-query";
import {
  AdminListRequestOfSettingFilter,
  ISettingListItem,
  ListResponseOfSettingListItem,
  SettingFilter,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import contextStore from "stores/contextStore";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import { useNavigate } from "react-router-dom";
import InheritedFromCell from "./InheritedFromCell";
import SettingValue from "./SettingValue";

const styles = {
  overriddenRow: {
    "& td:first-of-type": {
      boxShadow: "inset 3px 0 0 0",
      color: "primary.dark",
      fontWeight: 500,
    },
  },
  inheritedRow: {
    "& td": {
      color: "text.secondary",
    },
  },
};

const SettingsTable = () => {
  const { formatMessage: t } = useIntl();
  const { settingsDetailsUrl, settingService } = useGlobalContext();
  const navigate = useNavigate();
  const { organizationId } = contextStore.useState();

  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("setting.SettingGroup.Name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const searchTerm = persistanceStore.useState(
    (s) => s.settingsFilterSearchTerm
  );

  const groupSettingIdStored = persistanceStore.useState(
    (s) => s.settingGroupId
  );

  const settingTypeIdStored = persistanceStore.useState((s) => s.settingTypeId);

  const inheritanceStored = persistanceStore.useState(
    (s) => s.settingsInheritance
  );

  const debouncedSearchTerm = useDebounce(searchTerm?.trim(), 500);

  // Narrowing by inheritance shrinks the result set, so the current page can fall outside it. Resetting during
  // render rather than in an effect keeps the stale page from reaching the query and flashing an empty table.
  const [appliedInheritance, setAppliedInheritance] = useState(inheritanceStored);

  if (appliedInheritance !== inheritanceStored) {
    setAppliedInheritance(inheritanceStored);
    setPage(0);
  }

  const settingFilter = SettingFilter.fromJS({
    searchText: debouncedSearchTerm,
    typeId: settingTypeIdStored,
    settingGroupId: groupSettingIdStored,
    inheritance: inheritanceStored,
  });

  const filter = AdminListRequestOfSettingFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    parameters: settingFilter,
    sortField: orderBy,
    sortDescending: order === "desc",
  });

  const fetchSettings = () => settingService.getSettings(filter);

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfSettingListItem>(
    [
      "settings",
      organizationId,
      debouncedSearchTerm,
      order,
      orderBy,
      page,
      rowsPerPage,
      groupSettingIdStored,
      settingTypeIdStored,
      inheritanceStored,
    ],
    fetchSettings,
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "settingNameLabel" }),
      customSortAccessor: "setting.Name",
    },
    {
      accessor: "groupName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "groupLabel" }),
      customSortAccessor: "setting.SettingGroup.Name",
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "valueLabel" }),
      View: SettingValue,
      noSort: true,
    },
    {
      accessor: "inheritedOrganizationName",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "settingInheritedFromLabel" }),
      View: InheritedFromCell,
      noSort: true,
    },
  ];

  interface ISettingListItemRow extends ISettingListItem, LazyTableRow {
    id: string;
  }

  const rawSummary = (data?.data ?? []) as ISettingListItemRow[];

  const rowSx = (row: LazyTableRow) =>
    (row as ISettingListItemRow).isOverridden
      ? styles.overriddenRow
      : styles.inheritedRow;

  return (
    <LazyTable
      {...{
        rowCount: data?.totalRecords ?? 0,
        rows: rawSummary ?? [],
        loading: isFetching,
        dense: true,
        cells,
        page,
        setPage,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        rowsPerPage,
        setRowsPerPage,
        rowSx,
        noResultsLabel: t({ id: "noResults" }),
        onClick(id, row) {
          navigate(settingsDetailsUrl(row.settingId));
        },
        titleComponent: (
          <Box m={2}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item xs={12}>
                <Typography variant="h6">
                  <T id="settings" />
                </Typography>
              </Grid>
            </Grid>
          </Box>
        ),
        collapsable: true,
      }}
    />
  );
};

export default SettingsTable;
